import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

/**
 * The host's player.
 *
 * YouTube's terms require the embed to be at least 200x200, visible, and not
 * covered by anything — so this is a real, visible player with its own
 * controls, and the operator UI sits around it rather than on top of it.
 *
 * Only the operator mounts this. Everyone else gets a silent progress bar and
 * hears the audio over Discord, which is what makes ads and Spotify logins one
 * person's problem instead of the whole room's.
 *
 * Two things here exist because of one bug — the first play of the night
 * loading the video but never starting it:
 *
 * 1. The player is mounted as soon as there is a match, with the first entry
 *    cued, rather than being created when play is pressed. Building the player
 *    means fetching YouTube's iframe API over the network, and anything that
 *    happens after that fetch has lost the click that started it. Browsers only
 *    allow audio to start from a real user gesture, so the play call was being
 *    silently refused.
 * 2. `playNow` is imperative, so the button handler can call it *synchronously*
 *    inside the click. Driving playback from a `shouldPlay` prop meant the call
 *    happened in an effect after a server round trip — again, no gesture.
 *
 * An intent that arrives before the player is built is remembered and applied
 * on ready, which is the other half of why the second song always worked and
 * the first never did.
 */

interface YouTubePlayerApi {
  loadVideoById(options: { videoId: string; startSeconds?: number }): void;
  cueVideoById(options: { videoId: string; startSeconds?: number }): void;
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  destroy(): void;
}

export interface YouTubePlayerHandle {
  /** Starts a video immediately. Safe to call before the player exists. */
  playNow(videoId: string, startSeconds: number): void;
  /** Picks up where it was paused, without moving the position. */
  resumeNow(): void;
  pauseNow(): void;
  /** Where the player is now, in ms. Zero before it is ready. */
  getPositionMs(): number;
  /**
   * How long the loaded media is, in ms. Zero before it is ready.
   *
   * This is how the clip picker works without a YouTube API key: the server may
   * not know the length, but a loaded player always does.
   */
  getDurationMs(): number;
  seekToMs(positionMs: number): void;
}

declare global {
  interface Window {
    YT?: {
      Player: new (element: HTMLElement | string, config: unknown) => YouTubePlayerApi;
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number; BUFFERING: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<void> | null = null;

/** Loads the IFrame API once per page, however many players come and go. */
function loadYouTubeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<void>((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve();
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
  });

  return apiPromise;
}

export const YouTubePlayer = forwardRef<
  YouTubePlayerHandle,
  {
    /** What to have loaded. Cued and left paused until `playNow` is called. */
    videoId: string;
    startSeconds: number;
    /** What the server believes. Used to follow a pause, never to start audio. */
    shouldPlay: boolean;
    /** Server position in ms, used to correct drift on the host's own player. */
    targetPositionMs: number | null;
    /** True while the server is waiting to hear that playback really started. */
    awaitingStart: boolean;
    /** Reports the real start position, in ms, so the server can re-anchor. */
    onStarted?: (positionMs: number) => void;
    /**
     * Where this player actually is, a few times a second.
     *
     * In host-only mode this player *is* the audio, so it is the clock the room
     * follows — see the note on drift correction below.
     */
    onPosition?: (positionMs: number, isPlaying: boolean) => void;
  }
>(function YouTubePlayer(
  { videoId, startSeconds, shouldPlay, targetPositionMs, awaitingStart, onStarted, onPosition },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YouTubePlayerApi | null>(null);
  const loadedVideoRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);

  // A play pressed before the player finished building. Applied on ready.
  const pendingPlayRef = useRef<{ videoId: string; startSeconds: number } | null>(null);

  // Held in a ref so the player's own callbacks always see the current one
  // without tearing the iframe down and rebuilding it on every render.
  const onStartedRef = useRef(onStarted);
  onStartedRef.current = onStarted;
  const onPositionRef = useRef(onPosition);
  onPositionRef.current = onPosition;
  const awaitingStartRef = useRef(awaitingStart);
  awaitingStartRef.current = awaitingStart;

  const report = useCallback(() => {
    const player = playerRef.current;
    if (!player || !awaitingStartRef.current) return;
    onStartedRef.current?.(Math.round(player.getCurrentTime() * 1000));
  }, []);

  useEffect(() => {
    let disposed = false;

    void loadYouTubeApi().then(() => {
      if (disposed || !containerRef.current || !window.YT) return;

      const initial = pendingPlayRef.current;

      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: initial?.videoId ?? videoId,
        playerVars: {
          // No related videos at the end, and no YouTube chrome we do not need.
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          start: Math.floor(initial?.startSeconds ?? startSeconds),
        },
        events: {
          onReady: () => {
            if (disposed) return;
            loadedVideoRef.current = initial?.videoId ?? videoId;
            setReady(true);

            const queued = pendingPlayRef.current;
            pendingPlayRef.current = null;
            if (queued) {
              // The gesture is long gone by now, so this can still be refused —
              // but by the time anyone presses play the player is normally
              // already built, and this is the fallback for the race.
              playerRef.current?.playVideo();
            }
          },
          onStateChange: (event: { data: number }) => {
            if (event.data === window.YT?.PlayerState.PLAYING) report();
          },
        },
      });
    });

    return () => {
      disposed = true;
      playerRef.current?.destroy();
      playerRef.current = null;
      setReady(false);
    };
    // Mounted once. Video changes are handled below so the iframe is reused.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      playNow(nextVideoId: string, nextStartSeconds: number) {
        const player = playerRef.current;
        if (!player || !ready) {
          pendingPlayRef.current = { videoId: nextVideoId, startSeconds: nextStartSeconds };
          return;
        }

        // loadVideoById starts playing by itself; playVideo covers the case
        // where the same video is already loaded and merely paused.
        if (loadedVideoRef.current !== nextVideoId) {
          player.loadVideoById({ videoId: nextVideoId, startSeconds: nextStartSeconds });
          loadedVideoRef.current = nextVideoId;
        } else {
          player.seekTo(nextStartSeconds, true);
        }
        player.playVideo();
      },
      resumeNow() {
        playerRef.current?.playVideo();
      },
      pauseNow() {
        playerRef.current?.pauseVideo();
      },
      getPositionMs() {
        return Math.round((playerRef.current?.getCurrentTime() ?? 0) * 1000);
      },
      getDurationMs() {
        return Math.round((playerRef.current?.getDuration() ?? 0) * 1000);
      },
      seekToMs(positionMs: number) {
        playerRef.current?.seekTo(positionMs / 1000, true);
      },
    }),
    [ready],
  );

  /**
   * Reconciles a pause that this browser did not initiate.
   *
   * `pauseMode` lets anyone in the room stop the music, so the operator's
   * player has to follow a pause it never asked for. Play is deliberately not
   * reconciled here — starting audio belongs to the click handler, where the
   * browser will still allow it.
   */
  useEffect(() => {
    if (!ready || shouldPlay) return;
    playerRef.current?.pauseVideo();
  }, [shouldPlay, ready]);

  // Keep the idle player showing the entry that is up next, so the operator can
  // see what they are about to play — and so the first press has nothing left
  // to load.
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !ready || loadedVideoRef.current === videoId) return;
    if (pendingPlayRef.current) return;

    player.cueVideoById({ videoId, startSeconds });
    loadedVideoRef.current = videoId;
  }, [videoId, startSeconds, ready]);

  /**
   * Reporting where this player is, rather than dragging it to where the server
   * thinks it should be.
   *
   * The original version did the opposite: it micro-seeked this player to match
   * the server anchor. That is right for `synced` mode, where every listener has
   * their own player and the server is the only shared truth — but in host-only
   * mode *this* player is the sound in the room, and correcting it against the
   * server means yanking the audio everyone is listening to. It also made the
   * in-game timeline disagree with what people could hear whenever the operator
   * scrubbed the embed by hand, which is exactly the bug that was reported.
   *
   * So the arrow is reversed. This reports, the server re-anchors, and the
   * room's progress bar follows the audio.
   *
   * The same tick re-reports the start if the server is still waiting: a seek
   * during playback produces no PLAYING transition, so relying on the state
   * change alone would leave the room's clock frozen until the watchdog.
   */
  useEffect(() => {
    if (!ready) return;

    const timer = setInterval(() => {
      const player = playerRef.current;
      if (!player) return;

      const playing = player.getPlayerState() === window.YT?.PlayerState.PLAYING;

      if (awaitingStartRef.current) {
        if (playing) report();
        return;
      }

      onPositionRef.current?.(Math.round(player.getCurrentTime() * 1000), playing);
    }, 1000);

    return () => clearInterval(timer);
  }, [ready, report]);

  // Kept in the signature for `synced` mode, where the server is the authority
  // and per-client players do follow it. Unused in host-only mode by design.
  void targetPositionMs;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-black">
      {/* 16:9, and never smaller than YouTube's 200px minimum. */}
      <div className="relative aspect-video w-full min-h-[200px]">
        <div ref={containerRef} className="absolute inset-0 size-full" />
      </div>
    </div>
  );
});
